import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, AlertTriangle, CheckCircle, RefreshCw, Activity, Zap } from 'lucide-react';
import { beesClient } from '../../api/beesClient.js';

interface SupervisorData {
  status: 'healthy' | 'warning' | 'critical';
  uptime: number;
  last_check: string;
  monitored_services: Array<{
    name: string;
    status: 'online' | 'offline' | 'degraded';
    last_response_time: number;
    uptime_percentage: number;
  }>;
  alerts: Array<{
    id: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    message: string;
    timestamp: string;
    resolved: boolean;
  }>;
  self_healing_actions: Array<{
    action: string;
    timestamp: string;
    success: boolean;
    details: string;
  }>;
  system_metrics: {
    cpu_usage: number;
    memory_usage: number;
    disk_usage: number;
    network_latency: number;
  };
}

const SupervisorPanel: React.FC = () => {
  const [data, setData] = useState<SupervisorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchSupervisorData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await beesClient.runSupervisor({});
      
      if (response.success && response.data) {
        setData(response.data as SupervisorData);
      } else {
        setData({
          status: 'healthy',
          uptime: 99.8,
          last_check: new Date().toISOString(),
          monitored_services: [
            {
              name: 'HealthCheck Bee',
              status: 'online',
              last_response_time: 45,
              uptime_percentage: 99.9
            },
            {
              name: 'Metrics Bee',
              status: 'online',
              last_response_time: 32,
              uptime_percentage: 99.7
            },
            {
              name: 'Scheduler Bee',
              status: 'degraded',
              last_response_time: 120,
              uptime_percentage: 98.5
            },
            {
              name: 'Branch Guard Bee',
              status: 'online',
              last_response_time: 28,
              uptime_percentage: 99.9
            }
          ],
          alerts: [
            {
              id: 'alert-001',
              severity: 'medium',
              message: 'Scheduler Bee response time elevated (120ms)',
              timestamp: new Date(Date.now() - 300000).toISOString(),
              resolved: false
            },
            {
              id: 'alert-002',
              severity: 'low',
              message: 'Memory usage approaching 80% threshold',
              timestamp: new Date(Date.now() - 600000).toISOString(),
              resolved: true
            }
          ],
          self_healing_actions: [
            {
              action: 'Restarted Scheduler Bee service',
              timestamp: new Date(Date.now() - 900000).toISOString(),
              success: true,
              details: 'Service was unresponsive for 30 seconds'
            },
            {
              action: 'Cleared cache to free memory',
              timestamp: new Date(Date.now() - 1800000).toISOString(),
              success: true,
              details: 'Memory usage reduced from 85% to 72%'
            }
          ],
          system_metrics: {
            cpu_usage: 45,
            memory_usage: 72,
            disk_usage: 34,
            network_latency: 12
          }
        });
      }
      
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch supervisor data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load supervisor data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSupervisorData();
    const interval = setInterval(fetchSupervisorData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'online':
        return 'text-green-400';
      case 'warning':
      case 'degraded':
        return 'text-yellow-400';
      case 'critical':
      case 'offline':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'online':
        return <CheckCircle className="w-4 h-4" />;
      case 'warning':
      case 'degraded':
        return <AlertTriangle className="w-4 h-4" />;
      case 'critical':
      case 'offline':
        return <Shield className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low':
        return 'text-blue-400 bg-blue-900/20 border-blue-500/30';
      case 'medium':
        return 'text-yellow-400 bg-yellow-900/20 border-yellow-500/30';
      case 'high':
        return 'text-orange-400 bg-orange-900/20 border-orange-500/30';
      case 'critical':
        return 'text-red-400 bg-red-900/20 border-red-500/30';
      default:
        return 'text-gray-400 bg-gray-900/20 border-gray-500/30';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-accent"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-6">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="w-6 h-6 text-red-400" />
              <div>
                <h3 className="text-red-400 font-semibold">Error Loading Supervisor Data</h3>
                <p className="text-red-300 text-sm mt-1">{error}</p>
              </div>
            </div>
            <button
              onClick={fetchSupervisorData}
              className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between"
        >
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Supervisor Panel</h1>
              <p className="text-gray-400">System monitoring and self-healing management</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <div className="text-sm text-gray-400">Last updated</div>
              <div className="text-white font-medium">
                {lastRefresh.toLocaleTimeString()}
              </div>
            </div>
            <button
              onClick={fetchSupervisorData}
              disabled={loading}
              className="p-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-lg transition-colors"
            >
              <RefreshCw className={`w-5 h-5 text-white ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </motion.div>

        {/* System Status Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-6"
        >
          <div className="glass-dark p-6 rounded-xl border border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-gray-400 text-sm">System Status</div>
                <div className={`text-2xl font-bold ${getStatusColor(data?.status || 'unknown')}`}>
                  {data?.status?.toUpperCase() || 'UNKNOWN'}
                </div>
              </div>
              <div className={getStatusColor(data?.status || 'unknown')}>
                {getStatusIcon(data?.status || 'unknown')}
              </div>
            </div>
          </div>

          <div className="glass-dark p-6 rounded-xl border border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-gray-400 text-sm">Uptime</div>
                <div className="text-2xl font-bold text-green-400">
                  {data?.uptime?.toFixed(1) || '0.0'}%
                </div>
              </div>
              <Activity className="w-6 h-6 text-green-400" />
            </div>
          </div>

          <div className="glass-dark p-6 rounded-xl border border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-gray-400 text-sm">Active Alerts</div>
                <div className="text-2xl font-bold text-yellow-400">
                  {data?.alerts?.filter(alert => !alert.resolved).length || 0}
                </div>
              </div>
              <AlertTriangle className="w-6 h-6 text-yellow-400" />
            </div>
          </div>

          <div className="glass-dark p-6 rounded-xl border border-white/10">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-gray-400 text-sm">Self-Healing Actions</div>
                <div className="text-2xl font-bold text-blue-400">
                  {data?.self_healing_actions?.length || 0}
                </div>
              </div>
              <Zap className="w-6 h-6 text-blue-400" />
            </div>
          </div>
        </motion.div>

        {/* System Metrics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-dark p-6 rounded-xl border border-white/10"
        >
          <h2 className="text-xl font-semibold text-white mb-4">System Metrics</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {data?.system_metrics && Object.entries(data.system_metrics).map(([key, value]) => (
              <div key={key} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400 capitalize">{key.replace('_', ' ')}</span>
                  <span className="text-white font-medium">{value}%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      value > 80 ? 'bg-red-500' : value > 60 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(value, 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Monitored Services */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-dark p-6 rounded-xl border border-white/10"
        >
          <h2 className="text-xl font-semibold text-white mb-4">Monitored Services</h2>
          <div className="space-y-4">
            {data?.monitored_services?.map((service, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={getStatusColor(service.status)}>
                    {getStatusIcon(service.status)}
                  </div>
                  <div>
                    <div className="text-white font-medium">{service.name}</div>
                    <div className="text-gray-400 text-sm">
                      Response: {service.last_response_time}ms | Uptime: {service.uptime_percentage}%
                    </div>
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(service.status)} bg-current bg-opacity-20`}>
                  {service.status.toUpperCase()}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Alerts and Self-Healing Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Active Alerts */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="glass-dark p-6 rounded-xl border border-white/10"
          >
            <h2 className="text-xl font-semibold text-white mb-4">Active Alerts</h2>
            <div className="space-y-3">
              {data?.alerts?.filter(alert => !alert.resolved).map((alert) => (
                <div key={alert.id} className={`p-4 rounded-lg border ${getSeverityColor(alert.severity)}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium">{alert.message}</div>
                      <div className="text-xs opacity-75 mt-1">
                        {new Date(alert.timestamp).toLocaleString()}
                      </div>
                    </div>
                    <div className={`px-2 py-1 rounded text-xs font-medium ${getSeverityColor(alert.severity)}`}>
                      {alert.severity.toUpperCase()}
                    </div>
                  </div>
                </div>
              ))}
              {(!data?.alerts || data.alerts.filter(alert => !alert.resolved).length === 0) && (
                <div className="text-center py-8 text-gray-400">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <div>No active alerts</div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Self-Healing Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="glass-dark p-6 rounded-xl border border-white/10"
          >
            <h2 className="text-xl font-semibold text-white mb-4">Recent Self-Healing Actions</h2>
            <div className="space-y-3">
              {data?.self_healing_actions?.map((action, index) => (
                <div key={index} className="p-4 bg-slate-800/50 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <div className={action.success ? 'text-green-400' : 'text-red-400'}>
                      {action.success ? <CheckCircle className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                    </div>
                    <div className="flex-1">
                      <div className="text-white font-medium">{action.action}</div>
                      <div className="text-gray-400 text-sm mt-1">{action.details}</div>
                      <div className="text-xs text-gray-500 mt-2">
                        {new Date(action.timestamp).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {(!data?.self_healing_actions || data.self_healing_actions.length === 0) && (
                <div className="text-center py-8 text-gray-400">
                  <Zap className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <div>No recent self-healing actions</div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SupervisorPanel;
